import React, { ForwardedRef, forwardRef, ReactNode } from 'react';
import { DOMAttributes, DOMProps } from '@react-types/shared';

import {
  commonStyleHandlers,
  ResponsiveCssProp,
  SapphireCommonStyleProps,
  SapphireStyleProps,
  StyleHandlers,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import { GlobalDomAttributes } from '../../utils/types';
import { filterDOMProps } from '@react-aria/utils';

export interface GridStyleProps extends SapphireCommonStyleProps {
  /* Controls how the auto-placement algorithm works. Specifies how auto-placed items get flowed into the grid.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-flow).
   */
  gridAutoFlow?: ResponsiveCssProp<'gridAutoFlow'>;

  /* Specifies the size of an implicitly-created grid column track or pattern of tracks.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-columns).
   */
  gridAutoColumns?: ResponsiveCssProp<'gridAutoColumns'>;

  /* Specifies the size of an implicitly-created grid row track or pattern of tracks.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-auto-rows).
   */
  gridAutoRows?: ResponsiveCssProp<'gridAutoRows'>;

  /* Specifies named grid areas, establishing the cells in the grid and assigning them names.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-areas).
   */
  gridTemplateAreas?: ResponsiveCssProp<'gridTemplateAreas'>;

  /* Defines the line names and track sizing functions of the grid columns.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-columns).
   */
  gridTemplateColumns?: ResponsiveCssProp<'gridTemplateColumns'>;

  /* Defines the line names and track sizing functions of the grid rows.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/grid-template-rows).
   */
  gridTemplateRows?: ResponsiveCssProp<'gridTemplateRows'>;

  /* The space between rows and columns. It is a shorthand for rowGap and columnGap.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/gap).
   */
  gap?: ResponsiveCssProp<'gap', 'size'>;

  /* The space to display between a grid's columns.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/column-gap).
   */
  columnGap?: ResponsiveCssProp<'columnGap', 'size'>;

  /* The space to display between a grid's rows.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/row-gap).
   */
  rowGap?: ResponsiveCssProp<'rowGap', 'size'>;

  /* The space to display between a grid's rows.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-items).
   */
  justifyItems?: ResponsiveCssProp<'justifyItems'>;

  /* The space to display between a grid's columns.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/justify-content).
   */
  justifyContent?: ResponsiveCssProp<'justifyContent'>;

  /* Sets the align-self value on all direct children as a group
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/align-items).
   */
  alignItems?: ResponsiveCssProp<'alignItems'>;

  /* Sets the distribution of space between and around content items along the grid's block axis.
   * See [MDN](https://developer.mozilla.org/en-US/docs/Web/CSS/align-content).
   */
  alignContent?: ResponsiveCssProp<'alignContent'>;
  padding?: ResponsiveCssProp<'padding', 'size'>;
  paddingLeft?: ResponsiveCssProp<'padding', 'size'>;
  paddingRight?: ResponsiveCssProp<'paddingRight', 'size'>;
  paddingTop?: ResponsiveCssProp<'paddingTop', 'size'>;
  paddingBottom?: ResponsiveCssProp<'paddingBottom', 'size'>;
}
export interface GridProps
  extends DOMProps,
    Omit<
      DOMAttributes,
      'children' | 'style' | 'className' | 'dangerouslySetInnerHTML'
    >,
    SapphireStyleProps,
    GridStyleProps,
    GlobalDomAttributes {
  /** Children of the grid container. */
  children: ReactNode;
}

export const gridStylePropsHandler: StyleHandlers<GridStyleProps> = {
  ...commonStyleHandlers,
  gridAutoFlow: 'gridAutoFlow',
  gridAutoColumns: 'gridAutoColumns',
  gridAutoRows: 'gridAutoRows',
  gridTemplateAreas: 'gridTemplateAreas',
  gridTemplateColumns: 'gridTemplateColumns',
  gridTemplateRows: 'gridTemplateRows',
  gap: 'gap',
  columnGap: 'columnGap',
  rowGap: 'rowGap',
  justifyItems: 'justifyItems',
  justifyContent: 'justifyContent',
  alignItems: 'alignItems',
  alignContent: 'alignContent',
  padding: 'padding',
  paddingLeft: 'paddingLeft',
  paddingRight: 'paddingRight',
  paddingTop: 'paddingTop',
  paddingBottom: 'paddingBottom',
};

export const Grid = forwardRef(function Grid(
  props: GridProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  useThemeCheck();

  const { children, ...otherProps } = props;
  const { styleProps, filteredProps } = useSapphireStyleProps<
    Omit<GridProps, 'children'>,
    GridStyleProps
  >(otherProps, gridStylePropsHandler);

  const style = {
    ...styleProps.style,
    display: 'grid',
  };

  return (
    <div
      {...filteredProps}
      {...filterDOMProps(props, { global: true })}
      style={style}
      ref={ref}
      className={styleProps.className}
    >
      {children}
    </div>
  );
});
